/* eslint-disable @typescript-eslint/no-empty-function */
import { createContext, useContext, useState } from 'react';
import { ContextStatus } from '@leedpulse-monorepo/enums';
import DistributionType from '../../../../domain/enums/@shared/DistributionType';
import importContacts, { ImportContactsPayload } from '../../../../domain/use_cases/contacts/importContacts';
import getFileHeaders, { FileHeadersPayload } from '../../../../domain/use_cases/contacts/getFileHeaders';
import AuthenticationContext from '../../auth/AuthenticationContext';
import Subscription from '../../../../domain/models/Subscription';
import getSubscriptionById from '../../../../domain/use_cases/subscriptions/getSubscriptionById';
import UserDoc from '../../../../domain/models/UserDoc';
import getUsersBySubscriptionId from '../../../../domain/use_cases/users/getUsersBySubscriptionId';
import getScenariosByIds from '../../../../domain/use_cases/scenarios/getScenariosByIds';
import Scenario from '../../../../domain/models/Scenario';
import dayjs, { Dayjs } from 'dayjs';

const ContactsImportFormContext = createContext({
  status: ContextStatus.IDLE,
  statusHeaders: ContextStatus.IDLE,
  importMessage: '',
  subscription: null as Subscription | null,
  subscriptionOwners: [] as UserDoc[],
  subscriptionScenarios: [] as Scenario[],
  file: null as File | null,
  uploadedFileHeaders: [] as string[],
  uploadedFileMapping: [] as any[],
  distribution: DistributionType.none as DistributionType | null,
  owner: '',
  sender: '',
  prospectCategory: '',
  contactScheduled: true,
  contactScheduledDate: dayjs() as Dayjs | null,
  initWithSubscriptionId: (_: string, __: Scenario[] = []) => { },
  onFileChange: (_: File) => { },
  onFileRemove: () => { },
  onDistributionChange: (_: DistributionType) => { },
  onOwnerChange: (_: string) => { },
  onSenderChange: (_: string) => { },
  onFileMappingChange: (_: any[]) => { },
  onProspectCategoryChange: (_: string) => { },
  onContactScheduledChange: (_: boolean) => { },
  onContactScheduledDateChange: (_: Dayjs | null) => { },
  submit: () => { },
  reset: () => { },
  resetFileMapping: () => { },
  retrieveFileHeaders: () => { },
});

const fileMappingInitial = [
  {
    key: "civility",
    title: "Civilité",
    mapping: [],
    upto: 1,
    required: false,
  },
  {
    key: "firstname",
    title: "Prénom",
    mapping: [],
    upto: 1,
    required: false,
  },
  {
    key: "lastname",
    title: "Nom",
    mapping: [],
    upto: 1,
    required: false,
  },
  {
    key: "email",
    title: "Email",
    mapping: [],
    upto: 1,
    required: false,
  },
  {
    key: "company",
    title: "Entreprise",
    mapping: [],
    upto: 1,
    required: false,
  },
  {
    key: "adresse",
    title: "Adresse",
    mapping: [],
    upto: 1,
    required: false,
  },
  {
    key: "phone",
    title: "Téléphone",
    mapping: [],
    upto: 1,
    required: true,
  },
  {
    key: "details",
    title: "Contexte",
    mapping: [],
    upto: 9,
    required: false,
  }
];

export const ContactsImportFormProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [status, setStatus] = useState(ContextStatus.IDLE);
  const [statusHeaders, setStatusHeaders] = useState(ContextStatus.IDLE);
  const [subscription, setSubscription] = useState(null as Subscription | null);
  const [subscriptionOwners, setSubscriptionOwners] = useState([] as UserDoc[]);
  const [subscriptionScenarios, setSubscriptionScenarios] = useState([] as Scenario[]);
  const [file, setFile] = useState(null as File | null);
  const [distribution, setDistribution] = useState(DistributionType.on30Days as DistributionType | null);
  const [owner, setOwner] = useState('');
  const [sender, setSender] = useState('');
  const [prospectCategory, setProspectCategory] = useState('');
  const [importMessage, setImportMessage] = useState('');
  const [uploadedFileHeaders, setUploadedFileHeaders] = useState([] as string[]);
  const [uploadedFileMapping, setUploadedFileMapping] = useState(JSON.parse(JSON.stringify(fileMappingInitial)));
  const [contactScheduled, setContactScheduled] = useState(true);
  const [contactScheduledDate, setContactScheduledDate] = useState(dayjs() as Dayjs | null);

  const authContext = useContext(AuthenticationContext);

  const initWithSubscriptionId = async (subscriptionId: string, globalScenarios: Scenario[] = []) => {
    try {
      const subscription = await getSubscriptionById(subscriptionId);
      if (subscription === null) throw new Error('Subscription not found');
      const owners = await getUsersBySubscriptionId(subscriptionId);
      const scenarios = await getScenariosByIds(subscription.subscription.idScenarios || []);
      setSubscription(subscription?.subscription);
      setSubscriptionOwners(owners);
      setSubscriptionScenarios([...globalScenarios, ...scenarios]);
      if (owners.length < 2) {
        setOwner(owners[0].uid || '');
      }
      if (scenarios.length < 2) {
        setProspectCategory(scenarios[0]?.id || '');
      }
      if (subscription?.subscription?.sender.length < 2) {
        setSender(subscription?.subscription?.sender[0] || '');
      }
    } catch (e) {
      setStatus(ContextStatus.ERROR);
    }
  };

  const onFileChange = (file: File) => {
    setFile(file);
  };

  const onFileRemove = () => {
    setFile(null);
  };

  const onDistributionChange = (distribution: DistributionType) => {
    setDistribution(distribution);
  };

  const onOwnerChange = (owner: string) => {
    setOwner(owner);
  };

  const onSenderChange = (sender: string) => {
    setSender(sender);
  };

  const onFileMappingChange = (fileMapping: any[]) => {
    setUploadedFileMapping(fileMapping);
  }

  const onProspectCategoryChange = (prospectCategory: string) => {
    setProspectCategory(prospectCategory);
  };

  const onContactScheduledChange = (contactScheduled: boolean) => {
    setContactScheduled(contactScheduled);
  }

  const onContactScheduledDateChange = (contactScheduledDate: Dayjs | null) => {
    setContactScheduledDate(contactScheduledDate);
  }

  const retrieveFileHeaders = async () => {
    if (authContext.authentication === null) {
      return;
    }

    if (file === null) {
      return;
    }

    setStatusHeaders(ContextStatus.LOADING);

    try {
      const resFileHeaders = await getFileHeaders(
        new FileHeadersPayload(file),
        authContext.authentication
      );
      setUploadedFileHeaders(resFileHeaders?.data?.headers || []);
      setUploadedFileMapping(JSON.parse(JSON.stringify(fileMappingInitial)));
      setStatusHeaders(ContextStatus.SUCCESS);
    } catch (e) {
      setStatusHeaders(ContextStatus.ERROR);
    }
  }

  const resetFileMapping = () => {
    setUploadedFileMapping(JSON.parse(JSON.stringify(fileMappingInitial)));
  }

  const reset = () => {
    setStatus(ContextStatus.IDLE);
    setStatusHeaders(ContextStatus.IDLE);
    setSubscription(null);
    setSubscriptionOwners([]);
    setFile(null);
    setDistribution(DistributionType.none);
    setOwner('');
    setSender('');
    setProspectCategory('');
    setImportMessage('');
    setUploadedFileHeaders([]);
    setUploadedFileMapping([]);
    setContactScheduled(true);
    setContactScheduledDate(dayjs());
  }

  const submit = async () => {
    if (authContext.authentication === null) {
      return;
    }

    if (
      subscription === null ||
      file === null ||
      distribution === null ||
      owner === '' ||
      sender === ''
    ) {
      return;
    }
    setStatus(ContextStatus.LOADING);

    try {
      const resImport = await importContacts(
        new ImportContactsPayload(
          subscription!,
          file!,
          distribution!,
          owner,
          sender,
          prospectCategory,
          uploadedFileMapping,
          contactScheduled,
          contactScheduledDate
        ),
        authContext.authentication
      );
      setImportMessage(resImport?.data);
      setStatus(ContextStatus.SUCCESS);
    } catch (e: any) {
      setImportMessage(e.message || "Une erreur s'est produite lors de l'importation des contacts");
      setStatus(ContextStatus.ERROR);
    }
  };

  return (
    <ContactsImportFormContext.Provider
      value={{
        status,
        statusHeaders,
        importMessage,
        subscription,
        subscriptionOwners,
        subscriptionScenarios,
        file,
        uploadedFileHeaders,
        uploadedFileMapping,
        distribution,
        owner,
        sender,
        prospectCategory,
        contactScheduled,
        contactScheduledDate,
        initWithSubscriptionId,
        onFileChange,
        onFileRemove,
        onDistributionChange,
        onOwnerChange,
        onSenderChange,
        onFileMappingChange,
        onProspectCategoryChange,
        onContactScheduledChange,
        onContactScheduledDateChange,
        submit,
        reset,
        resetFileMapping,
        retrieveFileHeaders,
      }}
    >
      {children}
    </ContactsImportFormContext.Provider>
  );
};

export default ContactsImportFormContext;
